
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useQRCode } from "@/utils/qrCodeGenerator";
import { Button } from "@/components/ui/button";
import { Share2 } from 'lucide-react';
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useState } from "react";

interface QRModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  title: string;
  description: string;
}

const QRModal = ({ isOpen, onClose, url, title, description }: QRModalProps) => {
  const [actualUrl, setActualUrl] = useState(url);
  
  // ALWAYS check if the URL contains the special pattern
  useEffect(() => {
    if (url) {
      const specialPattern = '21d11b63-7b91-46fa-8c91-a30744574195';
      const redirectUrl = 'https://www.omooduduwa.org/join';
      
      // If URL contains our special pattern or has the special referrer ID
      if (url.includes(specialPattern) || 
          (url.includes('referrer=') && url.includes(specialPattern))) {
        console.log("QR Modal: Special URL detected, using redirect URL:", redirectUrl);
        setActualUrl(redirectUrl);
      } else {
        setActualUrl(url);
      }
    }
  }, [url]);
  
  // Generate QR code for the appropriate URL
  const qrCodeUrl = useQRCode(actualUrl || '');
  const isMobile = useIsMobile();
  
  const handleCopyLink = () => {
    if (!actualUrl) {
      toast.error("No URL available to copy");
      return;
    }
    
    navigator.clipboard.writeText(actualUrl)
      .then(() => {
        toast.success("Link copied to clipboard");
        console.log("Copied URL:", actualUrl);
      })
      .catch(() => toast.error("Failed to copy link"));
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md max-w-[92vw] mx-auto p-3 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-center text-lg sm:text-xl">{title}</DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col items-center justify-center space-y-4 py-2 w-full">
          {actualUrl ? (
            <>
              <div className="bg-white p-2 rounded-lg shadow-sm border flex justify-center items-center w-full">
                {qrCodeUrl ? (
                  <img 
                    src={qrCodeUrl} 
                    alt="QR Code" 
                    className={`${isMobile ? 'w-[80vw] h-[80vw] max-w-[300px] max-h-[300px]' : 'w-64 h-64'}`}
                  />
                ) : (
                  <div className="w-64 h-64 flex items-center justify-center text-gray-400">
                    Generating QR code...
                  </div>
                )}
              </div>
              
              <p className="text-sm text-center text-gray-500 max-w-xs">
                {description}
              </p>
              
              <div className="w-full flex flex-col items-center gap-3">
                <Button 
                  variant="outline" 
                  className="w-full max-w-xs" 
                  onClick={handleCopyLink}
                >
                  <Share2 size={16} className="mr-2" /> Copy Link
                </Button>
                
                <div className="w-full max-w-xs overflow-hidden">
                  <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded overflow-x-auto whitespace-nowrap break-all">
                    {actualUrl}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-500">
              No URL provided
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QRModal;
